import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Search, Building2, Trash2,
  Database, Loader2, Plus, RefreshCw, Users,
  ShieldCheck, ChevronLeft, ChevronRight, Layers3
} from "lucide-react";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import CompanyDetailsModal from "../components/companyDetailsModal";

const API = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/$/, "");

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

const PER_PAGE = 10;

const productLabel = (dbName) => {
  const name = String(dbName || "").toLowerCase();
  if (name.startsWith("infra")) return "Infra V5";
  return "Unclassified";
};

const productBadgeClass = (dbName) => {
  return productLabel(dbName) === "Infra V5"
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200";
};

const statusBadge = (active) =>
  active
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

const errorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong.";

export default function Admin_AllAdmins() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState("");

  const [coModalOpen, setCoModalOpen] = useState(false);
  const [coTenantId, setCoTenantId] = useState(null);
  const [coUsername, setCoUsername] = useState("");
  const [coCompanyName, setCoCompanyName] = useState("");

  const [page, setPage] = useState(1);

  const load = useCallback(async (signal) => {
    setLoading(true);

    try {
      const response = await axios.get(
        "https://supremeadmin.nissi-india.com/api/admins/all",
        {
          params: {
            page: 1,
            pageSize: 10000,
          },
          withCredentials: true,
          timeout: 20000,
        }
      );

      const data = response.data;

      setRows(
        Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.data?.rows)
          ? data.data.rows
          : []
      );
    } catch (error) {
      if (axios.isCancel(error)) return;

      console.error("Supreme Admin listing error:", error);

      if (error?.response?.status === 401) {
        sessionStorage.removeItem("infra_v5_supremeadmin");
        navigate("/supremeadmin/login", { replace: true });
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Unable to load admins",
        text: errorMessage(error),
        confirmButtonColor: "#059669",
      });
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return rows;

    return rows.filter((r) => {
      const fields = [
        r.tenantCode,
        r.databaseName,
        r.domain,
        r.username,
        r.company_name,
        r.email,
        r.phone,
        r.address,
        productLabel(r.databaseName),
      ];

      return fields.some((value) =>
        String(value ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [rows, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PER_PAGE)
  );

  const currentPage = Math.min(page, totalPages);
  const first = (currentPage - 1) * PER_PAGE;

  const current = filtered.slice(first, first + PER_PAGE);

  const totalTenants = new Set(
    rows.map((r) => r.tenantId ?? r.tenantCode)
  ).size;

  const totalLicenses = rows.reduce(
    (sum, r) => sum + (Number(r.num_licenses) || 0),
    0
  );

  const totalUsers = rows.reduce(
    (sum, r) => sum + (Number(r.num_users) || 0),
    0
  );

  const onSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const doDelete = async (tenantId, username) => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Delete Tenant Admin?",
      text: `This will permanently delete '${username}' from tenant #${tenantId}.`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    const key = `${tenantId}:${username}`;
    setDeleting(key);

    try {
      await api.delete(
        `/api/admins/${encodeURIComponent(tenantId)}/${encodeURIComponent(username)}`
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Tenant admin removed successfully.",
        confirmButtonColor: "#059669",
      });

      setPage(1);
      await load();
    } catch (error) {
      console.error("Delete admin error:", error);

      if (error?.response?.status === 401) {
        sessionStorage.removeItem("infra_v5_supremeadmin");
        navigate("/supremeadmin/login", { replace: true });
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: errorMessage(error),
        confirmButtonColor: "#059669",
      });
    } finally {
      setDeleting("");
    }
  };

  const openCompanyModal = (tenantId, username, companyName) => {
    setCoTenantId(tenantId);
    setCoUsername(username);
    setCoCompanyName(companyName || "");
    setCoModalOpen(true);
  };

  const thClass =
    "whitespace-nowrap px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wide";

  const tdClass = "whitespace-nowrap px-4 py-3.5 text-sm";

  const actionClass =
    "inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <div className="flex min-h-screen flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="min-w-0 w-full flex-1 overflow-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1700px]">

            {/* PAGE HEADER */}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                  <Database size={21} />
                </div>

                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
                    Infra V5 / Supreme Admin
                  </p>

                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    Tenant Admin Management
                  </h1>

                  <p className="mt-1 text-sm text-slate-500">
                    View and manage administrator accounts across tenants.
                  </p>
                </div>
              </div>

              <div className="text-sm text-slate-500">
                <Time />
              </div>
            </div>

            {/* SUMMARY */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                {
                  label: "Total Tenants",
                  value: totalTenants,
                  icon: Building2,
                },
                {
                  label: "Total Licenses",
                  value: totalLicenses,
                  icon: ShieldCheck,
                },
                {
                  label: "Total Users",
                  value: totalUsers,
                  icon: Users,
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.label}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-slate-500">
                          {item.label}
                        </p>
                        <p className="mt-2 text-2xl font-bold text-slate-900">
                          {item.value}
                        </p>
                      </div>

                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                        <Icon size={21} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* MAIN LIST */}
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 p-5">
                <div>
                  <h2 className="text-base font-bold text-slate-900">
                    All Tenant Administrators
                  </h2>

                  <p className="mt-1 text-xs text-slate-500">
                    {filtered.length} matching records
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPage(1);
                      load();
                    }}
                    disabled={loading}
                    className={actionClass}
                  >
                    <RefreshCw
                      size={15}
                      className={loading ? "animate-spin" : ""}
                    />
                    Refresh
                  </button>

                  <Link
                    to="/supremeadmin/tenant_Bootstrap"
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                  >
                    <Plus size={17} />
                    Add New
                  </Link>
                </div>
              </div>

              {/* SEARCH */}
              <div className="border-b border-slate-100 px-5 py-4">
                <div className="relative max-w-lg">
                  <Search
                    size={18}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="search"
                    value={search}
                    onChange={onSearch}
                    placeholder="Search tenant, database, username, company..."
                    className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
                  />
                </div>
              </div>

              {/* DATA TABLE */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1200px] border-collapse">
                  <thead className="bg-emerald-700 text-white">
                    <tr>
                      <th className={thClass}>S.No</th>
                      <th className={thClass}>Tenant</th>
                      <th className={thClass}>Product</th>
                      <th className={thClass}>Username</th>
                      <th className={thClass}>Company</th>
                      <th className={thClass}>Licenses</th>
                      <th className={thClass}>Users</th>
                      <th className={thClass}>Clients</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Modules</th>
                     
                      <th className={thClass}>Delete</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr>
                        <td colSpan={12} className="py-16 text-center">
                          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                            <Loader2
                              size={20}
                              className="animate-spin text-emerald-600"
                            />
                            Loading tenant administrators...
                          </div>
                        </td>
                      </tr>
                    ) : current.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="py-16 text-center">
                          <Database
                            size={30}
                            className="mx-auto mb-3 text-slate-300"
                          />
                          <p className="text-sm font-medium text-slate-600">
                            No Data Available
                          </p>
                          <p className="mt-1 text-xs text-slate-400">
                            No administrators match the current search.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      current.map((r, index) => {
                        const key = `${r.tenantId}:${r.username}`;
                        const product = productLabel(r.databaseName);

                        return (
                          <tr
                            key={key}
                            className="transition odd:bg-white even:bg-slate-50/70 hover:bg-emerald-50/40"
                          >
                            <td className={`${tdClass} text-slate-500`}>
                              {first + index + 1}
                            </td>

                            <td className={tdClass}>
                              <div className="flex items-center gap-2">
                                <Building2
                                  size={16}
                                  className="shrink-0 text-emerald-600"
                                />
                                <div>
                                  <p className="font-semibold text-slate-900">
                                    {r.tenantCode || "—"}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {r.databaseName || "—"}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className={tdClass}>
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${productBadgeClass(r.databaseName)}`}
                              >
                                {product}
                              </span>
                            </td>

                            <td className={`${tdClass} font-medium text-slate-900`}>
                              {r.username || "—"}
                            </td>

                            <td className={tdClass}>
                              <button
                                type="button"
                                onClick={() =>
                                  openCompanyModal(
                                    r.tenantId,
                                    r.username,
                                    r.company_name
                                  )
                                }
                                className="max-w-[180px] truncate text-left font-medium text-emerald-700 hover:underline"
                                title={r.company_name || ""}
                              >
                                {r.company_name || "—"}
                              </button>
                            </td>

                            <td className={tdClass}>
                              {r.num_licenses ?? 0}
                            </td>

                            <td className={tdClass}>
                              {r.num_users ?? "—"}
                            </td>

                            <td className={tdClass}>
                              {r.num_clients ?? 0}
                            </td>

                            <td className={tdClass}>
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadge(r.is_active)}`}
                              >
                                {r.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>

                            <td className={tdClass}>
                              <Link
                                to={`/supremeadmin/admin_module/${encodeURIComponent(r.username)}?tid=${encodeURIComponent(r.tenantId)}`}
                                className={actionClass}
                              >
                                <Layers3 size={14} />
                                Manage
                              </Link>
                            </td>

                           

                            <td className={tdClass}>
                              <button
                                type="button"
                                disabled={deleting === key}
                                onClick={() =>
                                  doDelete(r.tenantId, r.username)
                                }
                                className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-3 py-2 text-white transition hover:bg-rose-700 disabled:opacity-50"
                                title="Delete Admin"
                              >
                                {deleting === key ? (
                                  <Loader2
                                    size={16}
                                    className="animate-spin"
                                  />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
                <p className="text-xs text-slate-500">
                  Showing{" "}
                  {filtered.length ? first + 1 : 0}–
                  {Math.min(first + PER_PAGE, filtered.length)}{" "}
                  of {filtered.length} records
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={currentPage <= 1 || loading}
                    onClick={() =>
                      setPage((p) => Math.max(1, p - 1))
                    }
                    className={actionClass}
                  >
                    <ChevronLeft size={15} />
                    Previous
                  </button>

                  <span className="text-xs font-semibold text-slate-600">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= totalPages || loading}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    className={actionClass}
                  >
                    Next
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* COMPANY DETAILS MODAL */}
      <CompanyDetailsModal
        open={coModalOpen}
        tenantId={coTenantId}
        username={coUsername}
        companyName={coCompanyName}
        onClose={() => setCoModalOpen(false)}
      />
    </div>
  );
}